// VRP API service functions
import axios from 'axios';
import { 
  VRP_SOLVE_API, 
  VRP_ALGORITHMS_API
} from '../../../../config';

// Import unified color management system
import { globalColorManager } from '../../utils/ColorManager';

// Convert locations to GeoJSON
const convertLocationsToGeoJson = (locations) => {
  return {
    type: 'FeatureCollection',
    features: locations.map(loc => ({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [loc.longitude, loc.latitude] // GeoJSON format is [longitude,latitude]
      },
      properties: {
        id: loc.id,
        name: loc.name || ''
      }
    }))
  };
};

// Format locations for API
const formatLocationsForApi = (locations) => {
  return locations.map(loc => ({
    id: loc.id,
    lat: loc.latitude,
    lng: loc.longitude,
    name: loc.name
  }));
};

// Process vehicle path data from API response
const processVehiclePathsFromResponse = (responseData, locations) => {
  const isVrp = responseData.geojson?.properties?.is_vrp || false;
  const vehiclePathData = responseData.vehicle_paths || [];
  const formattedVehiclePaths = [];
  
  if (isVrp && vehiclePathData.length > 0) {
    // Process multi-vehicle paths
    for (const vehicle of vehiclePathData) {
      const pathObjects = mapPathIdsToLocations(vehicle.path, locations);
      
      if (pathObjects.length > 0) {
        formattedVehiclePaths.push({
          id: `vehicle_${vehicle.vehicle_id}`,
          vehicle_id: vehicle.vehicle_id,
          path: pathObjects,
          color: vehicle.color || getVehicleColor(vehicle.vehicle_id, vehicle),
          distance: vehicle.distance || 0
        });
      }
    }
  } else if (responseData.path && responseData.path.length > 0) {
    // Fallback to single vehicle (backward compatibility)
    const pathObjects = mapPathIdsToLocations(responseData.path, locations);
    
    if (pathObjects.length > 0) {
      formattedVehiclePaths.push({
        id: 'vehicle_1',
        vehicle_id: 1,
        path: pathObjects,
        color: getVehicleColor(1, { distance: responseData.distance }),
        distance: responseData.distance
      });
    }
  }
  
  // Ensure path property is always a valid array
  const path = Array.isArray(responseData.path) ? responseData.path : [];
  if (!Array.isArray(responseData.path)) {
    console.warn('Got weird path data back from API:', responseData.path);
  }
  
  return {
    path,
    vehiclePaths: formattedVehiclePaths,
    isVrp,
    distance: responseData.distance,
    time: responseData.duration,
    algorithm: responseData.algorithm,
    vehicleCount: responseData.vehicle_count,
    maxCapacity: responseData.max_capacity,
    minVehiclesNeeded: responseData.min_vehicles_needed,
    geojson: responseData.geojson
  };
};

// Map IDs to location objects
const mapPathIdsToLocations = (pathIds, locations) => {
  if (!pathIds || !Array.isArray(pathIds)) {
    return [];
  }
  
  return pathIds
    .map(pointId => {
      const location = locations.find(loc => loc.id === pointId);
      return location || null;
    })
    .filter(Boolean); // Filter out null values
};

// Solve VRP problem
export const solveVrp = async (groupId, locations, startId = null, algorithm = "capacitated_savings", useGeoJson = true, maxCapacity = 0) => {
  try {
    // Build request parameters
    const requestParams = buildVrpRequestParams(groupId, locations, startId, algorithm, useGeoJson, maxCapacity);
    
    // Call API
    const response = await callVrpApi(requestParams);
    
    if (response.data.success) {
      // Process the response data
      return processVehiclePathsFromResponse(response.data.data, locations);
    } else {
      console.error('Error returned from VRP solve API:', response.data.error);
      throw new Error(response.data.error || 'Error solving VRP');
    }
  } catch (error) {
    console.error('Error in solveVrp function:', error);
    throw error;
  }
};

// Build VRP request parameters
const buildVrpRequestParams = (groupId, locations, startId, algorithm, useGeoJson, maxCapacity) => {
  const requestParams = {
    group_id: groupId,
    params: {
      start_id: startId,
      algorithm: algorithm,
      max_capacity: maxCapacity
    }
  };

  if (useGeoJson) {
    // Convert to GeoJSON format
    const locationsGeoJson = convertLocationsToGeoJson(locations);
    requestParams.params.locations_geojson = locationsGeoJson;

  } else {
    // Use traditional format
    const formattedLocations = formatLocationsForApi(locations);
    requestParams.params.locations = formattedLocations;
  }
  
  return requestParams;
};

// Call VRP API
const callVrpApi = async (requestParams) => {
  try {
    return await axios.post(VRP_SOLVE_API, requestParams);
  } catch (error) {
    console.error('Error calling VRP solve API:', error);
    console.error('Request params were:', JSON.stringify(requestParams, null, 2));
    throw error;
  }
};

// Generate color using unified color management system
const getVehicleColor = (vehicleId, vehicleData = {}) => {
  return globalColorManager.getVehicleColor(vehicleId, vehicleData);
};

// Get VRP available algorithms
export const getVrpAlgorithms = async () => {
  try {
    const response = await axios.get(VRP_ALGORITHMS_API);
    
    if (response.data.success) {
      return response.data.data;
    } else {
      console.error('Error returned from VRP algorithms API:', response.data.error);
      throw new Error(response.data.error || 'Failed to get VRP algorithm information');
    }
  } catch (error) {
    console.error('Error calling VRP algorithms API:', error);
    throw error;
  }
}; 